import cloneDeep from 'lodash/cloneDeep';

import {insertAtIndex, removeByIndex, replaceByIndex} from 'common/util';

import {QueryGroupedEntry, QuerySuperEntry, RowAddress} from '../types';

/**
 * Immutably move an entry from one address to another.
 *
 * @return a copy of the new entry and entries list
 */
export function stageEntryMove(
  index: RowAddress,
  toIndex: RowAddress,
  entries: QuerySuperEntry[],
): {entry: QuerySuperEntry; entries: QuerySuperEntry[]} {
  let entry: QuerySuperEntry;

  if (isMovingFromGroupToGroup(index, toIndex)) {
    ({entries, entry} = moveBetweenGroups(index, toIndex, entries));
  } else if (isMovingIntoGroup(index, toIndex)) {
    ({entries, entry} = moveIntoGroup(index, toIndex, entries));
  } else if (isMovingOutOfGroup(index, toIndex)) {
    ({entries, entry} = moveOutOfGroup(index, toIndex, entries));
  } else if (isMovingTopLevelEntries(index, toIndex)) {
    ({entries, entry} = moveEntry(index, toIndex, entries));
  }

  return {entries, entry};
}

function sameGroup(a: RowAddress, b: RowAddress) {
  return a[0] === b[0];
}

function isMovingFromGroupToGroup(from: RowAddress, to: RowAddress) {
  return from.length === 2 && to.length === 2;
}
function isMovingIntoGroup(from: RowAddress, to: RowAddress) {
  return from.length === 1 && to.length === 2;
}
function isMovingOutOfGroup(from: RowAddress, to: RowAddress) {
  return from.length === 2 && to.length === 1;
}
function isMovingTopLevelEntries(from: RowAddress, to: RowAddress) {
  return from.length === 1 && to.length === 1;
}

function moveBetweenGroups(
  from: RowAddress,
  to: RowAddress,
  entries: QuerySuperEntry[],
): {entries: QuerySuperEntry[]; entry: QuerySuperEntry} {
  if (sameGroup(from, to)) {
    const groupEntry = entries[from[0]] as QueryGroupedEntry;
    const entry = cloneDeep(groupEntry.event[from[1]]);
    let groupEntries = groupEntry.event;
    // if we're moving it farther down in the list, correct the index to account for the missing spot
    const toIdx = to[1] > from[1] ? to[1] - 1 : to[1];
    groupEntries = removeByIndex(groupEntries, from[1]);
    groupEntries = insertAtIndex(groupEntries, toIdx, entry);
    entries = replaceByIndex(entries, to[0], {
      ...groupEntry,
      event: groupEntries,
    });
    return {entries, entry};
  } else {
    const fromGroup = entries[from[0]] as QueryGroupedEntry;
    const toGroup = entries[to[0]] as QueryGroupedEntry;
    const entry = cloneDeep(fromGroup.event[from[1]]);

    entries = replaceByIndex(entries, to[0], {
      ...toGroup,
      event: insertAtIndex(toGroup.event, to[1], entry),
    });

    let newFromGroupEntry = {
      ...fromGroup,
      event: removeByIndex(fromGroup.event, from[1]),
    };

    if (newFromGroupEntry.event.length === 1) {
      // if there's only one entry left, ungroup it
      newFromGroupEntry = newFromGroupEntry.event[0];
    }

    entries = replaceByIndex(entries, from[0], newFromGroupEntry);

    return {entries, entry};
  }
}

function moveIntoGroup(
  from: RowAddress,
  to: RowAddress,
  entries: QuerySuperEntry[],
): {entries: QuerySuperEntry[]; entry: QuerySuperEntry} {
  const entry = cloneDeep(entries[from[0]]);
  const toGroup = entries[to[0]] as QueryGroupedEntry;
  const toGroupEntries = insertAtIndex(toGroup.event, to[1], entry);
  entries = replaceByIndex(entries, to[0], {
    ...toGroup,
    event: toGroupEntries,
  });
  entries = removeByIndex(entries, from[0]);
  return {entries, entry};
}

function moveOutOfGroup(
  from: RowAddress,
  to: RowAddress,
  entries: QuerySuperEntry[],
): {entries: QuerySuperEntry[]; entry: QuerySuperEntry} {
  const fromGroup = entries[from[0]] as QueryGroupedEntry;
  const entry = cloneDeep(fromGroup.event[from[1]]);
  let newFromGroup = {
    ...fromGroup,
    event: removeByIndex(fromGroup.event, from[1]),
  };

  if (newFromGroup.event.length === 1) {
    // ungroup it if there's only one entry left
    newFromGroup = newFromGroup.event[0];
  }

  entries = replaceByIndex(entries, from[0], newFromGroup);
  entries = insertAtIndex(entries, to[0], entry);

  return {entries, entry};
}

function moveEntry(
  from: RowAddress,
  to: RowAddress,
  entries: QuerySuperEntry[],
): {entries: QuerySuperEntry[]; entry: QuerySuperEntry} {
  const entry = cloneDeep(entries[from[0]]);
  entries = removeByIndex(entries, from[0]);
  entries = insertAtIndex(entries, to[0], entry);
  return {entries, entry};
}
